import type {
  SanitySlugData,
  SanitySlugsData,
} from '@bulknutrients/bulk-shared-lib/interfaces/Sanity';
import { getAllSanitySlugs } from '@bulknutrients/bulk-shared-lib/lib/sanity/slugs';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { Container } from '@components/Container';
import { StandardLayout } from '@components/Layouts/StandardLayout';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { sitemapIncludeUrls } from '@data/sitemapIncludeUrls';
import type { GetStaticProps } from 'next';
import Head from 'next/head';
import type { ReactElement } from 'react';

function SlugList({
  slugs,
  title,
  headingLevel = 2,
}: {
  slugs: SanitySlugData[];
  title: string;
  headingLevel?: number;
}): ReactElement {
  return (
    <div>
      {headingLevel === 2 ? (
        <h2 className="capitalize">{title}</h2>
      ) : (
        <h3 className="capitalize">{title}</h3>
      )}
      <ul className="m-0 mb-5 ml-5 columns-3 p-0 pl-5">
        {slugs
          .slice() // Create a shallow copy of the array
          .sort((a, b) => a.title.localeCompare(b.title)) // Sort alphabetically by slug
          .map((urlObj) => (
            <li key={urlObj.slug} className="my-1">
              <TextLink href={urlObj.slug}>
                {urlObj.title || urlObj.slug}
              </TextLink>
            </li>
          ))}
      </ul>
    </div>
  );
}

export default function SiteMapPage({
  groupedUrls,
}: {
  groupedUrls: SanitySlugsData;
}): ReactElement {
  // Define the desired order of properties
  const order = [
    'products',
    'categories',
    'pages',
    'blogs',
    'ambassadors',
    'experts',
    'other',
  ];

  return (
    <>
      <Head>
        <title>Explore the Bulk Nutrients website with our sitemap</title>
        <meta
          name="title"
          content="Explore the Bulk Nutrients website with our sitemap"
        />
        <meta
          name="description"
          content="Explore our extensive Bulk Nutrients Australia sitemap. Easily navigate to find the product, category, page or ambassador on our site | Bulk Nutrients"
        />
        <link
          rel="canonical"
          href={`${process.env.NEXT_PUBLIC_SERVER_URL}/sitemap`}
        />
      </Head>
      <Container>
        <Breadcrumbs
          crumbs={[
            { title: 'Home', path: '/' },
            { title: 'Sitemap', path: '/sitemap' },
          ]}
        />
      </Container>
      <Container as="main" id="main-content">
        <h1>Bulk Nutrients' Website Sitemap</h1>
        {order.map((category) => {
          if (category === 'categories') {
            const childCategories =
              groupedUrls[category]?.reduce(
                (acc, curr) => {
                  const childCategory = curr.slug.split('/')[2];
                  if (!acc[childCategory]) {
                    acc[childCategory] = [curr];
                  } else {
                    acc[childCategory].push(curr);
                  }
                  return acc;
                },
                {} as Record<string, Array<SanitySlugData>>
              ) ?? {};

            const importantCategories = [
              'shop-by-diet',
              'shop-by-flavour',
              'shop-by-sport',
              'shop-by-ingredient',
            ];
            const otherCategories = Object.keys(childCategories)
              .filter((category) => !importantCategories.includes(category))
              .flatMap((category) => childCategories[category] ?? []);

            return (
              <>
                <h2 className="capitalize">Categories</h2>
                <SlugList
                  key="Shop by Diet Categories"
                  title="Shop by Diet Categories"
                  headingLevel={3}
                  slugs={
                    childCategories['shop-by-diet']
                      .slice() // Create a shallow copy of the array
                      .sort((a, b) => a.title.localeCompare(b.title)) // Sort alphabetically by slug
                  }
                />
                <SlugList
                  key="Shop by Flavour Categories"
                  title="Shop by Flavour Categories"
                  headingLevel={3}
                  slugs={
                    childCategories['shop-by-flavour']
                      .slice() // Create a shallow copy of the array
                      .sort((a, b) => a.title.localeCompare(b.title)) // Sort alphabetically by slug
                  }
                />
                <SlugList
                  key="Shop by Ingredient Categories"
                  title="Shop by Ingredient Categories"
                  headingLevel={3}
                  slugs={
                    childCategories['shop-by-ingredient']
                      .slice() // Create a shallow copy of the array
                      .sort((a, b) => a.title.localeCompare(b.title)) // Sort alphabetically by slug
                  }
                />
                <SlugList
                  key="Shop by Sport Categories"
                  title="Shop by Sport Categories"
                  headingLevel={3}
                  slugs={
                    childCategories['shop-by-sport']
                      .slice() // Create a shallow copy of the array
                      .sort((a, b) => a.title.localeCompare(b.title)) // Sort alphabetically by slug
                  }
                />
                <SlugList
                  key="Other Categories"
                  title="Other Categories"
                  headingLevel={3}
                  slugs={
                    otherCategories.sort((a, b) =>
                      a.title.localeCompare(b.title)
                    ) // Sort alphabetically by slug
                  }
                />
              </>
            );
          }
          if (category === 'blogs') {
            return groupedUrls.blogs ? (
              <>
                <h2 className="uppercase">Blogs</h2>
                {Object.keys(groupedUrls.blogs).map((blogCategory) => (
                  <SlugList
                    key={blogCategory}
                    title={blogCategory}
                    headingLevel={3}
                    slugs={
                      (groupedUrls.blogs as Record<string, SanitySlugData[]>)[
                        blogCategory
                      ]
                        .slice() // Create a shallow copy of the array
                        .sort((a, b) => a.title.localeCompare(b.title)) // Sort alphabetically by slug
                    }
                  />
                ))}
              </>
            ) : null;
          }
          return (
            <SlugList
              key={category}
              title={category}
              slugs={
                groupedUrls[category]
                  .slice() // Create a shallow copy of the array
                  .sort((a, b) => a.title.localeCompare(b.title)) // Sort alphabetically by slug
              }
            />
          );
        })}
      </Container>
    </>
  );
}

SiteMapPage.getLayout = function getLayout(page: ReactElement) {
  return <StandardLayout>{page}</StandardLayout>;
};

export const getStaticProps: GetStaticProps = async () => {
  // pages that are manually inputted in data/sitemapIncludeUrls
  const pages = sitemapIncludeUrls;

  const paths = await getAllSanitySlugs();

  // Put the manual URLs into their respective categories as taken from sanity or add to "other"
  for (const page of pages) {
    let addedToCategory = false;

    for (const category in paths) {
      // for in iterates properties, so our category keys
      const regex = new RegExp(`^.*${category}.*$`, 'i'); // create a regex to match the category anywhere in the slug
      if (page.slug.match(regex)) {
        paths[category].push(page);
        addedToCategory = true;
        break; // exit the loop since a category was found
      }
    }

    if (!addedToCategory) {
      if (!paths.other) {
        paths.other = []; // create an empty array if the "Other" object doesn't exist yet
      }
      paths.other.push(page);
    }
  }

  // Return the URLs as props
  return {
    props: { groupedUrls: paths },
    revalidate: 86400, // Revalidate the data every day
  };
};
